import { useEffect, useState } from "react";

import { getAppSettings, type AppSettingsResponse } from "api/appSettings";

const SESSION_KEY_DISMISSED = "upright-app-settings-prompt-dismissed";

let cachedIncomplete: boolean | null = null;
let inflight: Promise<boolean> | null = null;

function computeIncomplete(s: AppSettingsResponse): boolean {
  const noGovtFees = !s.govtFeeOptions || s.govtFeeOptions.length === 0;
  const noProfFee = !s.profFee || s.profFee === 0;
  const accountHolder = s.paymentDetails?.accountHolder ?? "";
  const accountNo = s.paymentDetails?.accountNo ?? "";
  const noPayment = !accountHolder.trim() || !accountNo.trim();
  return noGovtFees || noProfFee || noPayment;
}

interface UseAppSettingsCompletionCheckResult {
  incomplete: boolean;
  dismiss: () => void;
}

export function useAppSettingsCompletionCheck(
  enabled: boolean
): UseAppSettingsCompletionCheckResult {
  const [incomplete, setIncomplete] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled) return;
    if (sessionStorage.getItem(SESSION_KEY_DISMISSED) === "1") return;

    if (cachedIncomplete !== null) {
      setIncomplete(cachedIncomplete);
      return;
    }

    let cancelled = false;
    const promise =
      inflight ??
      (inflight = getAppSettings()
        .then(computeIncomplete)
        .catch(() => false));

    void promise.then((result) => {
      cachedIncomplete = result;
      if (!cancelled) setIncomplete(result);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY_DISMISSED, "1");
    setIncomplete(false);
  };

  return { incomplete, dismiss };
}
